// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import LoginPage from '@/pages/LoginPage'
import OrgsPage from '@/pages/OrgsPage'
import OnboardOrgPage from '@/pages/OnboardOrgPage'
import OrgDetailPage from '@/pages/OrgDetailPage'
import UsagePage from '@/pages/UsagePage'
import TeamPage from '@/pages/TeamPage'
import SettingsPage from '@/pages/SettingsPage'
import ConsoleLayout from '@/components/ConsoleLayout'
import RequireAuth from '@/components/RequireAuth'
import RequireRole from '@/components/RequireRole'
import { ThemeProvider } from '@/context/theme-context'
import { AuthProvider } from '@/context/auth-context'

// Wraps a console page in the auth guard + role guard + authenticated layout.
// `min` is the minimum role required; omitting it defaults to qh_admin.
function Protected({
  children,
  min = 'qh_admin',
}: {
  children: React.ReactNode
  min?: 'qh_admin' | 'platform_superadmin'
}) {
  return (
    <RequireAuth>
      <RequireRole min={min}>
        <ConsoleLayout>{children}</ConsoleLayout>
      </RequireRole>
    </RequireAuth>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/orgs" element={<Protected min="qh_admin"><OrgsPage /></Protected>} />
            <Route path="/orgs/new" element={<Protected min="qh_admin"><OnboardOrgPage /></Protected>} />
            <Route path="/orgs/:orgId" element={<Protected min="qh_admin"><OrgDetailPage /></Protected>} />
            <Route path="/usage" element={<Protected min="qh_admin"><UsagePage /></Protected>} />
            <Route path="/team" element={<Protected min="qh_admin"><TeamPage /></Protected>} />
            <Route path="/settings" element={<Protected min="platform_superadmin"><SettingsPage /></Protected>} />
            <Route path="/" element={<Navigate to="/orgs" replace />} />
            <Route path="*" element={<Navigate to="/orgs" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  )
}
